import { useSelector } from 'react-redux'
import { useBilling } from './hooks'
import { selectBillingEstimateForPlanChange } from './selectors/selectBillingEstimateForPlanChange'
import {
  BILLING_CYCLE_ANNUAL,
  BILLING_CYCLE_MONTHLY,
  PRICING_MODEL_USAGE,
} from './constants'
import {
  buildLineItemsFromPricings,
  calculateSubscriptionFlatTotal,
  calculateSubscriptionUsageTotal,
  quantityFor,
} from './util'

export const useAnnualOfferFigures = ({
  additionalAgents = 0,
  promotionPercentOff = null,
} = {}) => {
  const {
    plans,
    currentInbox,
    usage,
    appliedDiscount,
    isLoaded,
    isLoading,
    calculateEstimatedTotalBill,
  } = useBilling()
  const { currency, nextBillDate } = useSelector(
    selectBillingEstimateForPlanChange
  )
  const empty = {
    ready: false,
    monthlyTotal: null,
    annualTotal: null,
    yearlySaving: null,
    annualPerMonth: null,
    monthsFree: null,
    seats: null,
    planName: null,
    monthlySeatPrice: null,
    annualSeatPrice: null,
    isSeatBased: false,
    currency,
    nextBillDate,
    firstYear: null,
    yearOneKept: null,
    promotionDiscount: null,
  }
  const pricing = currentInbox?.pricing
  if (
    !isLoaded ||
    isLoading ||
    !pricing ||
    !plans?.length ||
    !usage ||
    !Number.isInteger(additionalAgents) ||
    additionalAgents < 0 ||
    !plans.every(
      plan =>
        plan.pricing &&
        Number.isFinite(plan.pricing.monthly) &&
        plan.pricing.monthly >= 0 &&
        Number.isFinite(plan.pricing.annual) &&
        plan.pricing.annual >= 0 &&
        (plan.pricing.pricingModel !== PRICING_MODEL_USAGE ||
          (Array.isArray(plan.pricing.features) &&
            Number.isFinite(usage[plan.pricing.usageFrom])))
    ) ||
    !Number.isFinite(pricing.monthly) ||
    !Number.isFinite(pricing.annual) ||
    pricing.monthly <= 0 ||
    pricing.annual <= 0
  )
    return empty

  const pricings = plans.map(plan => plan.pricing)
  const prospectiveUsage = additionalAgents
    ? { ...usage, users: usage.users + additionalAgents }
    : usage
  const pricingQuantitiesById = plans.reduce((quantities, plan) => {
    if (additionalAgents && plan.pricing.usageFrom === 'users') {
      return {
        ...quantities,
        [plan.pricing.id]: quantityFor(
          plan.pricing,
          pricings,
          prospectiveUsage
        ),
      }
    }
    if (plan.quantity != null) {
      return { ...quantities, [plan.pricing.id]: plan.quantity }
    }
    return quantities
  }, {})
  const totalFor = cycle => {
    if (!additionalAgents) return calculateEstimatedTotalBill(cycle)
    const lines = buildLineItemsFromPricings({
      pricings: [...pricings],
      billingCycle: cycle,
      usage: prospectiveUsage,
      pricingQuantitiesById,
      discount: appliedDiscount,
    })
    return Math.round(
      calculateSubscriptionFlatTotal(lines) +
        calculateSubscriptionUsageTotal(lines)
    )
  }
  const monthlyTotal = totalFor(BILLING_CYCLE_MONTHLY)
  const annualTotal = totalFor(BILLING_CYCLE_ANNUAL)
  const seats =
    pricingQuantitiesById[pricing.id] ??
    quantityFor(pricing, pricings, prospectiveUsage)
  if (
    ![monthlyTotal, annualTotal, seats].every(
      value => Number.isFinite(value) && value >= 0
    )
  )
    return empty

  if (!Number.isFinite(monthlyTotal * 12)) return empty
  const yearlySaving = Math.max(0, monthlyTotal * 12 - annualTotal)
  const hasPromotion =
    Number.isFinite(promotionPercentOff) &&
    promotionPercentOff > 0 &&
    promotionPercentOff <= 100
  const firstYear = hasPromotion
    ? Math.round(annualTotal * (1 - promotionPercentOff / 100))
    : null
  return {
    ready: monthlyTotal > 0,
    monthlyTotal,
    annualTotal,
    yearlySaving,
    annualPerMonth: annualTotal / 12,
    monthsFree:
      monthlyTotal > 0 ? (yearlySaving / monthlyTotal).toFixed(1) : null,
    seats,
    planName: pricing.name,
    monthlySeatPrice: pricing.monthly,
    annualSeatPrice: pricing.annual / 12,
    isSeatBased:
      pricing.pricingModel === PRICING_MODEL_USAGE &&
      pricing.usageFrom === 'users',
    currency,
    nextBillDate,
    firstYear,
    yearOneKept: hasPromotion ? monthlyTotal * 12 - firstYear : null,
    promotionDiscount: hasPromotion ? annualTotal - firstYear : null,
  }
}

export default useAnnualOfferFigures
